import { cardIdOrder, getCard, hashSeed, isNaturalRank, isWildcard, naturalRankIndex, rankStrength, resolveRuleProfile, createOfficialOpeningDeal, shuffleAndDeal, sortCardIds, } from "./cards.js";
import { classifyCombination, combinationBeats } from "./combinations.js";
import { enumerateLegalPlays } from "./legal.js";
import { NATURAL_RANKS, SEATS, InvalidGameActionError, } from "./types.js";
function teamOf(seat) {
    return (seat % 2);
}
/** Seats are displayed bottom, left, top, right for +0/+1/+2/+3.
 * Competitive Guandan play proceeds counter-clockwise, therefore the next
 * actor is the preceding numeric seat: bottom -> right -> top -> left. */
function nextSeat(seat) {
    return ((seat + 3) % 4);
}
/** Clockwise is used only by the official equal-rank double-tribute mapping. */
function clockwiseSeat(seat) {
    return ((seat + 1) % 4);
}
function nextActiveSeat(state, after) {
    let candidate = nextSeat(after);
    for (let count = 0; count < 4; count += 1) {
        if (!state.finishOrder.includes(candidate))
            return candidate;
        candidate = nextSeat(candidate);
    }
    throw new InvalidGameActionError("NO_ACTIVE_SEAT", "No unfinished player remains");
}
/** When a player wins the trick with their final cards, their unfinished
 * partner takes the next lead (接风). Only fall back to the next active seat
 * when that partner has already finished as well. */
function leadAfterFinishedWinner(state, winner) {
    const partner = ((winner + 2) % 4);
    return state.finishOrder.includes(partner)
        ? nextActiveSeat(state, winner)
        : partner;
}
function progressForRank(rank) {
    return naturalRankIndex(rank);
}
function rankForProgress(progress) {
    return NATURAL_RANKS[Math.min(NATURAL_RANKS.length - 1, Math.max(0, progress))];
}
function boundedOutcomeDelta(value) {
    return Math.min(3, Math.max(1, Math.abs(value)));
}
/**
 * Fixed-round matches compare final level, round wins, then team points.
 * A fully tied aggregate falls back to the final round deterministically.
 */
export function resolveMatchOutcome(state, finalRound) {
    if (state.ruleProfile.finishMode === "through-ace") {
        return Object.freeze({
            winningTeam: finalRound.winningTeam,
            levelDelta: finalRound.advancement,
            scoreDelta: finalRound.advancement,
            doubleUp: finalRound.doubleUp,
            outcomeBasis: "through-ace",
        });
    }
    const comparisons = [
        {
            difference: progressForRank(state.teamLevels[0]) -
                progressForRank(state.teamLevels[1]),
            basis: "final-level",
        },
        {
            difference: state.teamRoundWins[0] - state.teamRoundWins[1],
            basis: "round-wins",
        },
        {
            difference: state.teamPoints[0] - state.teamPoints[1],
            basis: "team-points",
        },
    ];
    const decisive = comparisons.find(({ difference }) => difference !== 0);
    const winningTeam = decisive
        ? decisive.difference > 0
            ? 0
            : 1
        : finalRound.winningTeam;
    const levelDelta = decisive
        ? boundedOutcomeDelta(decisive.difference)
        : finalRound.advancement;
    return Object.freeze({
        winningTeam,
        levelDelta,
        scoreDelta: levelDelta,
        doubleUp: winningTeam === finalRound.winningTeam && finalRound.doubleUp,
        outcomeBasis: decisive?.basis ?? "final-round-tiebreak",
    });
}
function freezeHands(hands) {
    return Object.freeze({
        0: Object.freeze([...hands[0]]),
        1: Object.freeze([...hands[1]]),
        2: Object.freeze([...hands[2]]),
        3: Object.freeze([...hands[3]]),
    });
}
function freezePlayers(players) {
    return Object.freeze({
        0: Object.freeze({ ...players[0] }),
        1: Object.freeze({ ...players[1] }),
        2: Object.freeze({ ...players[2] }),
        3: Object.freeze({ ...players[3] }),
    });
}
function createPlayers(controls, hands) {
    return freezePlayers(Object.fromEntries(SEATS.map((seat) => [
        seat,
        {
            seat,
            team: teamOf(seat),
            remainingCards: hands[seat].length,
            finishOrder: null,
            control: controls?.[seat] ?? "human",
            timeoutCount: 0,
        },
    ])));
}
function event(seq, version, type, at, payload) {
    return Object.freeze({
        seq,
        version,
        type,
        at,
        payload: freezePayload(payload),
    });
}
function freezePayload(payload) {
    const freezeValue = (value) => {
        if (Array.isArray(value))
            return Object.freeze(value.map(freezeValue));
        if (value !== null && typeof value === "object") {
            return Object.freeze(Object.fromEntries(Object.entries(value).map(([key, nested]) => [
                key,
                freezeValue(nested),
            ])));
        }
        return value;
    };
    return freezeValue(payload);
}
function actionTime(state, action) {
    return action.at ?? state.version * 1_000 + 1_000;
}
function resultWithEvents(state, emitted) {
    return Object.freeze({
        state: Object.freeze({
            ...state,
            events: Object.freeze([...state.events, ...emitted]),
        }),
        events: Object.freeze(emitted),
    });
}
export function createGameState(options = {}) {
    const seed = String(options.seed ?? "guandan-default-seed");
    const dealDirection = options.dealDirection ?? "counter-clockwise";
    const officialOpening = options.dealerSeat === undefined && dealDirection === "counter-clockwise"
        ? createOfficialOpeningDeal(seed)
        : null;
    const dealerSeat = options.dealerSeat ?? officialOpening?.dealerSeat ?? 0;
    const firstSeat = options.dealerSeat ?? officialOpening?.firstSeat ?? dealerSeat;
    const levelRank = options.levelRank ?? options.teamLevels?.[0] ?? "2";
    const teamLevels = options.teamLevels ?? [levelRank, levelRank];
    const teamProgress = options.teamProgress ??
        [progressForRank(teamLevels[0]), progressForRank(teamLevels[1])];
    const aceFailureCounts = options.aceFailureCounts ?? [0, 0];
    const ruleProfile = resolveRuleProfile(options.ruleProfile);
    const now = options.now ?? 0;
    const turnDurationMs = options.turnDurationMs ?? 60_000;
    const normalizedOptions = Object.freeze({
        ...options,
        seed,
        dealDirection,
        levelRank,
        teamLevels,
        teamProgress,
        aceFailureCounts,
        ruleProfile,
        now,
        turnDurationMs,
    });
    const deal = officialOpening ?? shuffleAndDeal(seed, dealerSeat, dealDirection);
    const hands = freezeHands({
        0: deal.hands[0],
        1: deal.hands[1],
        2: deal.hands[2],
        3: deal.hands[3],
    });
    const matchId = options.matchId ?? `match-${hashSeed(seed).toString(16).padStart(8, "0")}`;
    const initialEvents = Object.freeze([
        event(0, 0, "GAME_CREATED", now, { options: normalizedOptions }),
        event(1, 0, "CARDS_DEALT", now, {
            roundNumber: 1,
            dealerSeat,
            firstSeat,
            revealedCardId: officialOpening?.revealedCardId ?? null,
            dealDirection,
            seed,
        }),
    ]);
    return Object.freeze({
        matchId,
        seed,
        version: 0,
        phase: "playing",
        ruleProfile,
        turnDurationMs,
        roundNumber: 1,
        dealerSeat,
        levelRank,
        teamLevels: Object.freeze([teamLevels[0], teamLevels[1]]),
        teamProgress: Object.freeze([teamProgress[0], teamProgress[1]]),
        aceFailureCounts: Object.freeze([
            aceFailureCounts[0],
            aceFailureCounts[1],
        ]),
        teamRoundWins: Object.freeze([0, 0]),
        teamPoints: Object.freeze([0, 0]),
        hands,
        players: createPlayers(options.controls, hands),
        currentSeat: firstSeat,
        deadlineAt: now + turnDurationMs,
        lastPlay: null,
        passCount: 0,
        turnIndex: 0,
        finishOrder: Object.freeze([]),
        playedCards: Object.freeze([]),
        tribute: null,
        previousSettlement: null,
        events: initialEvents,
        createOptions: normalizedOptions,
    });
}
function assertPlayingTurn(state, seat) {
    if (state.phase !== "playing")
        throw new InvalidGameActionError("INVALID_PHASE", "Cards can only be played in playing phase");
    if (state.currentSeat !== seat)
        throw new InvalidGameActionError("NOT_YOUR_TURN", `Current seat is ${state.currentSeat}`);
    if (state.finishOrder.includes(seat))
        throw new InvalidGameActionError("ALREADY_FINISHED", "Finished players cannot act");
}
function settleRound(state, finishOrder, at, version) {
    if (finishOrder.length !== 4)
        throw new Error("Round settlement requires all four seats");
    const order = finishOrder;
    const winningTeam = teamOf(order[0]);
    const doubleUp = teamOf(order[1]) === winningTeam;
    const advancement = doubleUp
        ? 3
        : teamOf(order[2]) === winningTeam
            ? 2
            : 1;
    const beforeProgress = state.teamProgress[winningTeam];
    // A must be played as its own level. Reaching A from K never skips the A
    // round, and winning at A only completes the match when the winner's partner
    // is second or third (advancement 2/3), as required by the competition rule.
    const passedAce = beforeProgress >= NATURAL_RANKS.length - 1 && advancement >= 2;
    const afterProgress = passedAce
        ? NATURAL_RANKS.length
        : Math.min(NATURAL_RANKS.length - 1, beforeProgress + advancement);
    const levelBefore = rankForProgress(beforeProgress);
    const levelAfter = rankForProgress(afterProgress);
    const teamProgress = [...state.teamProgress];
    const teamLevels = [...state.teamLevels];
    const aceFailureCounts = [...state.aceFailureCounts];
    const teamRoundWins = [...state.teamRoundWins];
    const teamPoints = [...state.teamPoints];
    teamProgress[winningTeam] = afterProgress;
    teamLevels[winningTeam] = levelAfter;
    teamRoundWins[winningTeam] += 1;
    teamPoints[winningTeam] += advancement;
    const aceResetTeams = [];
    if (state.levelRank === "A" && !passedAce) {
        for (const team of [0, 1]) {
            if (state.teamLevels[team] !== "A")
                continue;
            aceFailureCounts[team] += 1;
            if (aceFailureCounts[team] < 3)
                continue;
            aceFailureCounts[team] = 0;
            teamProgress[team] = 0;
            teamLevels[team] = "2";
            aceResetTeams.push(team);
        }
    }
    if (passedAce)
        aceFailureCounts[winningTeam] = 0;
    const settlement = Object.freeze({
        roundNumber: state.roundNumber,
        finishOrder: Object.freeze([...order]),
        winningTeam,
        advancement,
        doubleUp,
        levelBefore,
        levelAfter,
        passedAce,
    });
    const matchSettled = state.ruleProfile.finishMode === "through-ace"
        ? passedAce
        : state.roundNumber >= state.ruleProfile.fixedRounds;
    const next = Object.freeze({
        ...state,
        version,
        phase: matchSettled ? "match-settled" : "settled",
        currentSeat: null,
        deadlineAt: null,
        finishOrder: Object.freeze([...order]),
        teamProgress: Object.freeze(teamProgress),
        aceFailureCounts: Object.freeze(aceFailureCounts),
        teamLevels: Object.freeze(teamLevels),
        teamRoundWins: Object.freeze(teamRoundWins),
        teamPoints: Object.freeze(teamPoints),
        previousSettlement: settlement,
    });
    const baseSeq = state.events.length;
    const emitted = [
        event(baseSeq, version, "ROUND_SETTLED", at, { settlement }),
        event(baseSeq + 1, version, "LEVEL_ADVANCED", at, {
            winningTeam,
            advancement,
            levelBefore,
            levelAfter,
            passedAce,
        }),
    ];
    for (const team of aceResetTeams) {
        emitted.push(event(baseSeq + emitted.length, version, "ACE_RESET", at, {
            team,
            failedAttempts: 3,
            levelBefore: "A",
            levelAfter: "2",
        }));
    }
    if (matchSettled) {
        const outcome = resolveMatchOutcome(next, settlement);
        emitted.push(event(baseSeq + emitted.length, version, "MATCH_SETTLED", at, {
            teamLevels: next.teamLevels,
            teamRoundWins: next.teamRoundWins,
            teamPoints: next.teamPoints,
            ...outcome,
        }));
    }
    return resultWithEvents(next, emitted);
}
function playCards(state, action) {
    assertPlayingTurn(state, action.seat);
    const unique = new Set(action.cardIds);
    if (action.cardIds.length === 0 || unique.size !== action.cardIds.length) {
        throw new InvalidGameActionError("INVALID_CARDS", "A play needs distinct physical cards");
    }
    const hand = state.hands[action.seat];
    if (action.cardIds.some((cardId) => !hand.includes(cardId))) {
        throw new InvalidGameActionError("CARD_NOT_OWNED", "The acting player does not own every selected card");
    }
    const context = {
        levelRank: state.levelRank,
        ruleProfile: state.ruleProfile,
    };
    const combination = classifyCombination(action.cardIds, context);
    if (!combination ||
        !combinationBeats(combination, state.lastPlay?.combination ?? null, context)) {
        throw new InvalidGameActionError("INVALID_COMBINATION", "Selected cards are not a legal winning combination");
    }
    const at = actionTime(state, action);
    const version = state.version + 1;
    const remaining = hand.filter((cardId) => !unique.has(cardId));
    const hands = {
        ...state.hands,
        [action.seat]: Object.freeze(remaining),
    };
    const players = { ...state.players };
    const finishOrder = [...state.finishOrder];
    const baseSeq = state.events.length;
    const emitted = [
        event(baseSeq, version, "ACTION_APPLIED", at, { action }),
        event(baseSeq + 1, version, "CARDS_PLAYED", at, {
            seat: action.seat,
            cardIds: combination.cardIds,
            combination,
        }),
    ];
    if (remaining.length === 0) {
        finishOrder.push(action.seat);
        const order = finishOrder.length;
        players[action.seat] = Object.freeze({
            ...players[action.seat],
            remainingCards: 0,
            finishOrder: order,
        });
        emitted.push(event(baseSeq + emitted.length, version, "PLAYER_FINISHED", at, {
            seat: action.seat,
            order,
        }));
    }
    else {
        players[action.seat] = Object.freeze({
            ...players[action.seat],
            remainingCards: remaining.length,
        });
    }
    let next = Object.freeze({
        ...state,
        version,
        hands: freezeHands(hands),
        players: freezePlayers(players),
        lastPlay: Object.freeze({
            seat: action.seat,
            cardIds: combination.cardIds,
            combination,
            turnIndex: state.turnIndex,
        }),
        passCount: 0,
        turnIndex: state.turnIndex + 1,
        finishOrder: Object.freeze([...finishOrder]),
        playedCards: Object.freeze([...state.playedCards, ...combination.cardIds]),
    });
    const firstFinished = finishOrder[0];
    const secondFinished = finishOrder[1];
    const doubleUpDecided = finishOrder.length === 2 &&
        firstFinished !== undefined &&
        secondFinished !== undefined &&
        teamOf(firstFinished) === teamOf(secondFinished);
    if (doubleUpDecided || finishOrder.length === 3) {
        let candidate = nextSeat(action.seat);
        while (finishOrder.length < 4) {
            if (!finishOrder.includes(candidate)) {
                finishOrder.push(candidate);
                players[candidate] = Object.freeze({
                    ...players[candidate],
                    finishOrder: finishOrder.length,
                });
            }
            candidate = nextSeat(candidate);
        }
        next = Object.freeze({
            ...next,
            players: freezePlayers(players),
            finishOrder: Object.freeze(finishOrder),
        });
        const settled = settleRound(next, finishOrder, at, version);
        const settlementEvents = settled.events.map((item, index) => event(baseSeq + emitted.length + index, item.version, item.type, item.at, item.payload));
        const finalWithoutEvents = Object.freeze({
            ...settled.state,
            events: state.events,
        });
        return resultWithEvents(finalWithoutEvents, [
            ...emitted,
            ...settlementEvents,
        ]);
    }
    const currentSeat = nextActiveSeat(next, action.seat);
    next = Object.freeze({
        ...next,
        currentSeat,
        deadlineAt: at + state.turnDurationMs,
    });
    return resultWithEvents(next, emitted);
}
function passTurn(state, action) {
    assertPlayingTurn(state, action.seat);
    if (!state.lastPlay)
        throw new InvalidGameActionError("CANNOT_PASS", "The leading player cannot pass");
    const at = actionTime(state, action);
    const version = state.version + 1;
    const baseSeq = state.events.length;
    const emitted = [
        event(baseSeq, version, "ACTION_APPLIED", at, { action }),
        event(baseSeq + 1, version, "PLAYER_PASSED", at, { seat: action.seat }),
    ];
    const activeCount = 4 - state.finishOrder.length;
    const passCount = state.passCount + 1;
    const requiredPasses = activeCount - (state.finishOrder.includes(state.lastPlay.seat) ? 0 : 1);
    if (passCount >= requiredPasses) {
        const lead = state.finishOrder.includes(state.lastPlay.seat)
            ? leadAfterFinishedWinner(state, state.lastPlay.seat)
            : state.lastPlay.seat;
        const next = Object.freeze({
            ...state,
            version,
            currentSeat: lead,
            deadlineAt: at + state.turnDurationMs,
            lastPlay: null,
            passCount: 0,
            turnIndex: state.turnIndex + 1,
        });
        emitted.push(event(baseSeq + 2, version, "TRICK_RESET", at, { leadSeat: lead }));
        return resultWithEvents(next, emitted);
    }
    const next = Object.freeze({
        ...state,
        version,
        currentSeat: nextActiveSeat(state, action.seat),
        deadlineAt: at + state.turnDurationMs,
        passCount,
        turnIndex: state.turnIndex + 1,
    });
    return resultWithEvents(next, emitted);
}
export function legalTributeCards(cards, levelRank) {
    // The heart level cards are wildcards (逢人配) and are explicitly excluded
    // from tribute. Either physical copy of the strongest remaining rank is
    // legal, so the player can choose which copy to reveal.
    const eligible = cards.filter((cardId) => !isWildcard(cardId, levelRank));
    if (eligible.length === 0)
        return Object.freeze([]);
    const maximum = Math.max(...eligible.map((cardId) => rankStrength(getCard(cardId).rank, levelRank)));
    return Object.freeze(eligible
        .filter((cardId) => rankStrength(getCard(cardId).rank, levelRank) === maximum)
        .sort(cardIdOrder));
}
/** Jokers are unambiguous mandatory tribute cards and can be handed over by
 * the authority automatically. Without a joker, the donor chooses manually
 * among the legal maximum cards (the heart level wildcard is already absent). */
export function automaticTributeCard(cards, levelRank) {
    const cardId = legalTributeCards(cards, levelRank)[0];
    if (!cardId)
        return null;
    const rank = getCard(cardId).rank;
    return rank === "BJ" || rank === "SJ" ? cardId : null;
}
export function qualifiesForAntiTribute(hands, donors, requiredBigJokers) {
    if (requiredBigJokers <= 0)
        return false;
    const count = donors
        .flatMap((seat) => hands[seat])
        .filter((cardId) => getCard(cardId).rank === "BJ").length;
    return count >= requiredBigJokers;
}
export function legalReturnTributeCards(cards, levelRank) {
    const preferred = cards.filter((cardId) => {
        const card = getCard(cardId);
        return (isNaturalRank(card.rank) &&
            card.rank !== levelRank &&
            naturalRankIndex(card.rank) <= naturalRankIndex("10"));
    });
    if (preferred.length > 0)
        return Object.freeze(preferred.sort((left, right) => rankStrength(getCard(left).rank, levelRank) -
            rankStrength(getCard(right).rank, levelRank) ||
            cardIdOrder(left, right)));
    if (cards.length === 0)
        return Object.freeze([]);
    const minimum = Math.min(...cards.map((cardId) => rankStrength(getCard(cardId).rank, levelRank)));
    return Object.freeze([...cards]
        .filter((cardId) => rankStrength(getCard(cardId).rank, levelRank) === minimum)
        .sort(cardIdOrder));
}
function resolveTributeTransfers(state, offers) {
    const tribute = state.tribute;
    if (!tribute)
        throw new Error("Missing tribute state");
    if (!tribute.doubleDown) {
        const offer = offers[0];
        return Object.freeze([
            { ...offer, to: tribute.previousFirst, returnCardId: null },
        ]);
    }
    const sorted = [...offers].sort((left, right) => {
        const strength = rankStrength(getCard(right.cardId).rank, state.levelRank) -
            rankStrength(getCard(left.cardId).rank, state.levelRank);
        if (strength !== 0)
            return strength;
        return cardIdOrder(left.cardId, right.cardId);
    });
    if (rankStrength(getCard(sorted[0]?.cardId).rank, state.levelRank) ===
        rankStrength(getCard(sorted[1]?.cardId).rank, state.levelRank)) {
        // Official competition rules use clockwise mapping only when the two
        // tribute ranks are equal; the donor mapped to the previous first leads.
        return Object.freeze(offers.map((offer) => ({
            ...offer,
            to: clockwiseSeat(offer.from),
            returnCardId: null,
        })));
    }
    const first = sorted[0];
    const second = sorted[1];
    return Object.freeze([
        { ...first, to: tribute.previousFirst, returnCardId: null },
        {
            ...second,
            to: ((tribute.previousFirst + 2) % 4),
            returnCardId: null,
        },
    ]);
}
function offerTribute(state, action) {
    if (state.phase !== "tribute" || !state.tribute)
        throw new InvalidGameActionError("INVALID_PHASE", "No tribute is pending");
    if (!state.tribute.donors.includes(action.seat) ||
        state.tribute.offers.some((offer) => offer.from === action.seat)) {
        throw new InvalidGameActionError("INVALID_TRIBUTE_ACTOR", "This seat does not owe an outstanding tribute");
    }
    const hand = state.hands[action.seat];
    if (!hand.includes(action.cardId))
        throw new InvalidGameActionError("CARD_NOT_OWNED", "Tribute card is not in donor hand");
    if (!legalTributeCards(hand, state.levelRank).includes(action.cardId)) {
        throw new InvalidGameActionError("TRIBUTE_NOT_MAXIMUM", "Tribute must be a maximum-strength single card");
    }
    const at = actionTime(state, action);
    const version = state.version + 1;
    const baseSeq = state.events.length;
    const offers = Object.freeze([
        ...state.tribute.offers,
        { from: action.seat, cardId: action.cardId },
    ]);
    const emitted = [
        event(baseSeq, version, "ACTION_APPLIED", at, { action }),
        event(baseSeq + 1, version, "TRIBUTE_OFFERED", at, {
            from: action.seat,
            cardId: action.cardId,
            tributeKind: state.tribute.kind,
        }),
    ];
    if (offers.length < state.tribute.donors.length) {
        return resultWithEvents(Object.freeze({
            ...state,
            version,
            tribute: Object.freeze({ ...state.tribute, offers }),
            deadlineAt: at + state.turnDurationMs,
        }), emitted);
    }
    const transfers = resolveTributeTransfers(state, offers);
    const hands = { ...state.hands };
    for (const transfer of transfers) {
        hands[transfer.from] = Object.freeze(hands[transfer.from].filter((cardId) => cardId !== transfer.cardId));
        hands[transfer.to] = Object.freeze([
            ...hands[transfer.to],
            transfer.cardId,
        ]);
        emitted.push(event(baseSeq + emitted.length, version, "TRIBUTE_RECEIVED", at, {
            from: transfer.from,
            to: transfer.to,
            cardId: transfer.cardId,
            tributeKind: state.tribute.kind,
        }));
    }
    const next = Object.freeze({
        ...state,
        version,
        hands: freezeHands(hands),
        tribute: Object.freeze({ ...state.tribute, offers, transfers }),
        deadlineAt: at + state.turnDurationMs,
    });
    return resultWithEvents(next, emitted);
}
function returnTribute(state, action) {
    if (state.phase !== "tribute" ||
        !state.tribute ||
        state.tribute.transfers.length === 0) {
        throw new InvalidGameActionError("INVALID_PHASE", "No return tribute is pending");
    }
    const transfer = state.tribute.transfers.find((candidate) => candidate.to === action.seat &&
        candidate.from === action.to &&
        candidate.returnCardId === null);
    if (!transfer)
        throw new InvalidGameActionError("INVALID_RETURN_ACTOR", "This return relationship is not pending");
    if (!state.hands[action.seat].includes(action.cardId))
        throw new InvalidGameActionError("CARD_NOT_OWNED", "Return card is not owned");
    if (!legalReturnTributeCards(state.hands[action.seat], state.levelRank).includes(action.cardId)) {
        throw new InvalidGameActionError("INVALID_RETURN_CARD", "Return card must be no higher than 10, or the lowest card when no such card exists");
    }
    const at = actionTime(state, action);
    const version = state.version + 1;
    const baseSeq = state.events.length;
    const hands = { ...state.hands };
    hands[action.seat] = Object.freeze(hands[action.seat].filter((cardId) => cardId !== action.cardId));
    hands[action.to] = Object.freeze([...hands[action.to], action.cardId]);
    const transfers = state.tribute.transfers.map((candidate) => candidate === transfer
        ? Object.freeze({ ...candidate, returnCardId: action.cardId })
        : candidate);
    const complete = transfers.every((candidate) => candidate.returnCardId !== null);
    const tribute = Object.freeze({
        ...state.tribute,
        transfers: Object.freeze(transfers),
    });
    const emitted = [
        event(baseSeq, version, "ACTION_APPLIED", at, { action }),
        event(baseSeq + 1, version, "TRIBUTE_RETURNED", at, {
            from: action.seat,
            to: action.to,
            cardId: action.cardId,
            tributeKind: state.tribute.kind,
        }),
    ];
    const next = Object.freeze({
        ...state,
        version,
        phase: complete ? "playing" : state.phase,
        hands: freezeHands(hands),
        tribute,
        currentSeat: complete
            ? (tribute.transfers.find((candidate) => candidate.to === tribute.previousFirst)?.from ?? tribute.previousFirst)
            : null,
        deadlineAt: at + state.turnDurationMs,
    });
    return resultWithEvents(next, emitted);
}
function startNextRound(state, action) {
    if (state.phase !== "settled" || !state.previousSettlement) {
        throw new InvalidGameActionError("INVALID_PHASE", "A new round can only start after a non-final settlement");
    }
    const at = actionTime(state, action);
    const version = state.version + 1;
    const roundNumber = state.roundNumber + 1;
    const roundSeed = `${state.seed}:round:${roundNumber}`;
    const first = state.previousSettlement.finishOrder[0];
    const firstDrawSeat = state.previousSettlement.doubleUp
        ? nextSeat(first)
        : state.previousSettlement.finishOrder[3];
    const deal = shuffleAndDeal(roundSeed, firstDrawSeat, state.createOptions.dealDirection ?? "counter-clockwise");
    const hands = freezeHands({
        0: deal.hands[0],
        1: deal.hands[1],
        2: deal.hands[2],
        3: deal.hands[3],
    });
    const losingTeam = (1 - state.previousSettlement.winningTeam);
    const donors = state.previousSettlement.doubleUp
        ? state.previousSettlement.finishOrder.filter((seat) => teamOf(seat) === losingTeam)
        : [state.previousSettlement.finishOrder[3]];
    const tributeKind = state.previousSettlement.doubleUp
        ? "double"
        : teamOf(donors[0]) === teamOf(first)
            ? "inner"
            : "single";
    const bigJokers = donors
        .flatMap((seat) => hands[seat])
        .filter((cardId) => getCard(cardId).rank === "BJ").length;
    const antiTribute = qualifiesForAntiTribute(hands, donors, state.ruleProfile.antiTributeBigJokerCount);
    const tribute = antiTribute
        ? null
        : Object.freeze({
            previousFirst: first,
            doubleDown: state.previousSettlement.doubleUp,
            kind: tributeKind,
            donors: Object.freeze(donors),
            offers: Object.freeze([]),
            transfers: Object.freeze([]),
        });
    const players = createPlayers(Object.fromEntries(SEATS.map((seat) => [seat, state.players[seat].control])), hands);
    const baseSeq = state.events.length;
    const emitted = [
        event(baseSeq, version, "ACTION_APPLIED", at, { action }),
        event(baseSeq + 1, version, "CARDS_DEALT", at, {
            roundNumber,
            dealerSeat: firstDrawSeat,
            firstSeat: antiTribute ? first : null,
            dealDirection: state.createOptions.dealDirection ?? "counter-clockwise",
            seed: roundSeed,
        }),
    ];
    if (antiTribute) {
        emitted.push(event(baseSeq + 2, version, "ANTI_TRIBUTE", at, {
            donors,
            bigJokers,
            firstSeat: first,
        }));
    }
    const next = Object.freeze({
        ...state,
        version,
        phase: antiTribute ? "playing" : "tribute",
        roundNumber,
        dealerSeat: firstDrawSeat,
        levelRank: state.teamLevels[state.previousSettlement.winningTeam],
        hands,
        players,
        currentSeat: antiTribute ? first : null,
        deadlineAt: at + state.turnDurationMs,
        lastPlay: null,
        passCount: 0,
        turnIndex: 0,
        finishOrder: Object.freeze([]),
        playedCards: Object.freeze([]),
        tribute,
    });
    return resultWithEvents(next, emitted);
}
function changeControl(state, seat, control, at) {
    const version = state.version + 1;
    const baseSeq = state.events.length;
    const players = {
        ...state.players,
        [seat]: Object.freeze({ ...state.players[seat], control }),
    };
    const action = { type: "SET_CONTROL", seat, control, at };
    return resultWithEvents(Object.freeze({ ...state, version, players: freezePlayers(players) }), [
        event(baseSeq, version, "ACTION_APPLIED", at, { action }),
        event(baseSeq + 1, version, "CONTROL_CHANGED", at, { seat, control }),
    ]);
}
function timeout(state, action) {
    if (state.phase !== "playing" && state.phase !== "tribute")
        throw new InvalidGameActionError("INVALID_PHASE", "Timeout cannot be applied in this phase");
    if (state.players[action.seat].control !== "bot")
        throw new InvalidGameActionError("INVALID_ACTION", "A human seat must choose its own play, tribute, and return card unless trustee is enabled");
    const at = actionTime(state, action);
    let fallback;
    if (state.phase === "playing") {
        assertPlayingTurn(state, action.seat);
        const legal = enumerateLegalPlays(state.hands[action.seat], state.lastPlay?.combination ?? null, { levelRank: state.levelRank, ruleProfile: state.ruleProfile }, { physicalVariants: false });
        fallback = state.lastPlay
            ? { type: "PASS", seat: action.seat, at }
            : {
                type: "PLAY",
                seat: action.seat,
                cardIds: legal[0].cardIds,
                at,
            };
    }
    else if (state.tribute) {
        if (state.tribute.donors.includes(action.seat) &&
            !state.tribute.offers.some((offer) => offer.from === action.seat)) {
            const cardId = legalTributeCards(state.hands[action.seat], state.levelRank)[0];
            fallback = { type: "TRIBUTE", seat: action.seat, cardId, at };
        }
        else {
            const transfer = state.tribute.transfers.find((candidate) => candidate.to === action.seat && candidate.returnCardId === null);
            if (!transfer)
                throw new InvalidGameActionError("NOT_YOUR_TURN", "This seat has no timed-out tribute action");
            const cardId = legalReturnTributeCards(state.hands[action.seat], state.levelRank)[0];
            if (!cardId)
                throw new InvalidGameActionError("NO_RETURN_CARD", "No legal return card is available");
            fallback = {
                type: "RETURN_TRIBUTE",
                seat: action.seat,
                to: transfer.from,
                cardId,
                at,
            };
        }
    }
    else
        throw new Error("Unreachable timeout phase");
    const applied = applyGameAction(state, fallback);
    const players = { ...applied.state.players };
    const timeoutCount = players[action.seat].timeoutCount + 1;
    const takeover = timeoutCount >= state.ruleProfile.timeoutTakeoverAfter;
    players[action.seat] = Object.freeze({
        ...players[action.seat],
        timeoutCount,
        control: takeover ? "bot" : players[action.seat].control,
    });
    const rewrittenEvents = applied.events.map((item) => item.type === "ACTION_APPLIED"
        ? event(item.seq, item.version, item.type, item.at, { action })
        : item);
    rewrittenEvents.push(event(state.events.length + rewrittenEvents.length, applied.state.version, "TIMEOUT_APPLIED", at, { seat: action.seat, fallback, timeoutCount, takeover }));
    if (takeover) {
        rewrittenEvents.push(event(state.events.length + rewrittenEvents.length, applied.state.version, "CONTROL_CHANGED", at, {
            seat: action.seat,
            control: "bot",
            reason: "timeouts",
        }));
    }
    const events = Object.freeze([...rewrittenEvents]);
    const stateEvents = [...state.events, ...events];
    return Object.freeze({
        state: Object.freeze({
            ...applied.state,
            players: freezePlayers(players),
            events: Object.freeze(stateEvents),
        }),
        events,
    });
}
export function applyGameAction(state, action) {
    switch (action.type) {
        case "PLAY":
            return playCards(state, action);
        case "PASS":
            return passTurn(state, action);
        case "TRIBUTE":
            return offerTribute(state, action);
        case "RETURN_TRIBUTE":
            return returnTribute(state, action);
        case "START_NEXT_ROUND":
            return startNextRound(state, action);
        case "SET_CONTROL":
            return changeControl(state, action.seat, action.control, actionTime(state, action));
        case "TIMEOUT":
            return timeout(state, action);
    }
}
export function projectViewerState(state, selfSeat) {
    return Object.freeze({
        matchId: state.matchId,
        version: state.version,
        phase: state.phase,
        roundNumber: state.roundNumber,
        levelRank: state.levelRank,
        teamLevels: state.teamLevels,
        aceFailureCounts: state.aceFailureCounts,
        selfSeat,
        selfHand: selfSeat === null
            ? Object.freeze([])
            : Object.freeze([...state.hands[selfSeat]]),
        players: Object.freeze(SEATS.map((seat) => Object.freeze({ ...state.players[seat] }))),
        currentSeat: state.currentSeat,
        deadlineAt: state.deadlineAt,
        lastPlay: state.lastPlay,
        passCount: state.passCount,
        finishOrder: Object.freeze([...state.finishOrder]),
        playedCards: Object.freeze([...state.playedCards]),
        tribute: state.tribute,
        eventSeq: state.events.length - 1,
    });
}
export function createBotObservation(state, seat) {
    return Object.freeze({
        matchId: state.matchId,
        version: state.version,
        phase: state.phase,
        roundNumber: state.roundNumber,
        seat,
        team: teamOf(seat),
        ownHand: Object.freeze([...state.hands[seat]]),
        players: Object.freeze(SEATS.map((playerSeat) => Object.freeze({ ...state.players[playerSeat] }))),
        currentSeat: state.currentSeat,
        levelRank: state.levelRank,
        ruleProfile: state.ruleProfile,
        lastPlay: state.lastPlay,
        passCount: state.passCount,
        finishOrder: Object.freeze([...state.finishOrder]),
        playedCards: Object.freeze([...state.playedCards]),
        tribute: state.tribute,
    });
}
export function replayGameActions(options, actions) {
    let state = createGameState(options);
    for (const action of actions)
        state = applyGameAction(state, action).state;
    return state;
}
export function replayGameEvents(events) {
    const created = events.find((item) => item.type === "GAME_CREATED");
    const options = created?.payload.options;
    if (!options || typeof options !== "object")
        throw new InvalidGameActionError("INVALID_REPLAY", "Replay has no create options");
    const actions = events
        .filter((item) => item.type === "ACTION_APPLIED")
        .map((item) => item.payload.action)
        .filter((action) => typeof action === "object" && action !== null && "type" in action);
    const firstDeal = events.find((item) => item.type === "CARDS_DEALT");
    const recordedDirection = firstDeal?.payload.dealDirection;
    const replayOptions = {
        ...options,
        // Records created before the direction field existed were dealt clockwise.
        dealDirection: recordedDirection === "counter-clockwise"
            ? "counter-clockwise"
            : "clockwise",
    };
    return replayGameActions(replayOptions, actions);
}
export function replayFingerprint(state) {
    const payload = JSON.stringify({
        version: state.version,
        phase: state.phase,
        roundNumber: state.roundNumber,
        levelRank: state.levelRank,
        teamProgress: state.teamProgress,
        hands: state.hands,
        currentSeat: state.currentSeat,
        lastPlay: state.lastPlay,
        finishOrder: state.finishOrder,
        tribute: state.tribute,
    });
    return hashSeed(payload).toString(16).padStart(8, "0");
}
export function sortedHand(state, seat) {
    return sortCardIds(state.hands[seat], {
        levelRank: state.levelRank,
        ruleProfile: state.ruleProfile,
    });
}
//# sourceMappingURL=state.js.map